import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  CheckSquare,
  Activity,
  CalendarDays,
  FolderKanban,
  FileText,
  ShieldCheck,
  Users,
  Network,
  Building2,
  Brain,
  BookOpen,
  Server,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Settings,
} from 'lucide-react';
import { useUiStore } from '../../stores/uiStore';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const MAIN_NAV: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
  { path: '/jobs', label: 'Jobs', icon: <Briefcase size={20} /> },
  { path: '/tasks', label: 'Tasks', icon: <CheckSquare size={20} /> },
  { path: '/activity', label: 'Activity', icon: <Activity size={20} /> },
  { path: '/calendar', label: 'Calendar', icon: <CalendarDays size={20} /> },
  { path: '/projects', label: 'Projects', icon: <FolderKanban size={20} /> },
  { path: '/content', label: 'Content', icon: <FileText size={20} /> },
];

const GOVERNANCE_NAV: NavItem[] = [
  { path: '/approvals', label: 'Approvals', icon: <ShieldCheck size={20} /> },
];

const ORG_NAV: NavItem[] = [
  { path: '/agents', label: 'Agents', icon: <Users size={20} /> },
  { path: '/organization', label: 'Org Chart', icon: <Network size={20} /> },
  { path: '/office', label: 'Office', icon: <Building2 size={20} /> },
];

const KNOWLEDGE_NAV: NavItem[] = [
  { path: '/memory', label: 'Memory', icon: <Brain size={20} /> },
  { path: '/docs', label: 'Docs', icon: <BookOpen size={20} /> },
];

const SYSTEM_NAV: NavItem[] = [
  { path: '/vps', label: 'VPS', icon: <Server size={20} /> },
  { path: '/sessions', label: 'Sessions', icon: <MessageSquare size={20} /> },
];

function NavSection({ label, items, collapsed, onNavClick }: { label: string; items: NavItem[]; collapsed: boolean; onNavClick: () => void }) {
  return (
    <div className="mb-2">
      {collapsed ? (
        <div className="h-5" />
      ) : (
        <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
          {label}
        </p>
      )}
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.path}>
            <NavLink
              to={item.path}
              end={item.path === '/'}
              onClick={onNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-accent-purple/15 text-accent-purple'
                    : 'text-text-secondary hover:bg-white/5'
                } ${collapsed ? 'justify-center' : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={isActive ? 'text-accent-purple' : 'text-text-muted'}>
                    {item.icon}
                  </span>
                  {collapsed ? null : <span>{item.label}</span>}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);

  const closeMobileSidebar = () => setMobileSidebarOpen(false);

  return (
    <aside
      className={`flex flex-col h-full bg-bg-sidebar border-r border-border-subtle transition-[width] duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo area */}
      <div className="flex items-center gap-2 h-14 px-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-accent-purple/20 text-accent-purple flex-shrink-0">
          <Settings size={18} />
        </div>
        {collapsed ? null : (
          <span className="text-sm font-semibold text-text-primary truncate">
            Mission Control
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <NavSection label="Main" items={MAIN_NAV} collapsed={collapsed} onNavClick={closeMobileSidebar} />
        <NavSection label="Governance" items={GOVERNANCE_NAV} collapsed={collapsed} onNavClick={closeMobileSidebar} />
        <NavSection label="Organization" items={ORG_NAV} collapsed={collapsed} onNavClick={closeMobileSidebar} />
        <NavSection label="Knowledge" items={KNOWLEDGE_NAV} collapsed={collapsed} onNavClick={closeMobileSidebar} />
        <NavSection label="System" items={SYSTEM_NAV} collapsed={collapsed} onNavClick={closeMobileSidebar} />
      </nav>

      {/* Collapse toggle — hidden on mobile */}
      <button
        onClick={toggleSidebar}
        className="hidden md:flex items-center justify-center h-10 border-t border-border-subtle text-text-muted hover:text-text-secondary hover:bg-white/5 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>
    </aside>
  );
}
